import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Pencil, Copy, Trash2, ArrowUp, ArrowDown, Upload, Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, erroApi } from './api';
import { getHome } from './homeApi';
import HomeActions from './HomeActions';

const BANNER_VAZIO = {
  id: '', nomeInterno: '', modo: 'imagem', ativo: true,
  imagemDesktop: '', imagemMobile: '', enquadramento: 'cobrir', posicaoImagem: 'centro',
  titulo: '', subtitulo: '', descricao: '', botaoPrimario: '', botaoSecundario: '',
  corTitulo: '#ffffff', corTexto: '#ffffff', corBotao: '#E63946', corBotaoTexto: '#ffffff',
  alinhamento: 'esquerda', posicaoVertical: 'centro', overlay: 'escura', altura: 'media',
  destinoTipo: 'nenhum', destinoValor: '', inicio: '', fim: '',
};

const paraInput = (iso) => (iso ? iso.slice(0, 16) : '');
const paraIso = (v) => (v ? `${v}:00-03:00` : '');

function UploadBotao({ rotulo, valor, onOk, testid }) {
  const ref = useRef(null);
  const [subindo, setSubindo] = useState(false);
  const enviar = async (lista) => {
    if (!lista?.length) return;
    setSubindo(true);
    const fd = new FormData();
    fd.append('files', lista[0]);
    const r = await apiFetch('/admin/upload?tipo=banner', { method: 'POST', body: fd });
    setSubindo(false);
    if (r.ok) {
      const data = await r.json();
      onOk(data.files[0].url);
      toast.success('Imagem enviada.');
    } else {
      toast.error(erroApi((await r.json().catch(() => ({}))).detail));
    }
    if (ref.current) ref.current.value = '';
  };
  return (
    <div>
      <label className="text-[11px] font-bold uppercase text-slate-400">{rotulo}</label>
      <div className="mt-1 flex items-center gap-2">
        {valor && <img src={valor} alt="" className="h-14 w-24 rounded-lg object-cover" />}
        <button type="button" data-testid={testid} disabled={subindo} onClick={() => ref.current?.click()} className="inline-flex items-center gap-1.5 rounded-full bg-mk-blue px-3.5 py-2 text-xs font-bold text-white hover:bg-mk-bluelight disabled:opacity-60">
          <Upload size={13} /> {subindo ? 'Enviando...' : valor ? 'Substituir' : 'Enviar imagem'}
        </button>
      </div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => enviar(e.target.files)} />
    </div>
  );
}

export default function Banners() {
  const { data: homeData, isLoading } = useQuery({ queryKey: ['admin-home'], queryFn: getHome });
  const { data: produtosData } = useQuery({
    queryKey: ['admin-produtos-picker', ''],
    queryFn: async () => (await apiFetch('/admin/produtos?limit=50')).json(),
  });
  const { data: cats } = useQuery({
    queryKey: ['admin-categorias'],
    queryFn: async () => (await apiFetch('/admin/categorias')).json(),
  });
  const [cfg, setCfg] = useState(null);
  const [editando, setEditando] = useState(null);
  const [excluindo, setExcluindo] = useState(null);

  useEffect(() => {
    if (homeData) setCfg(JSON.parse(JSON.stringify(homeData.rascunho)));
  }, [homeData]);

  if (isLoading || !cfg) return <p className="text-sm text-slate-400">Carregando...</p>;
  const banners = cfg.banners;

  const salvarLista = (lista) => setCfg({ ...cfg, banners: lista });
  const mover = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= banners.length) return;
    const lista = [...banners];
    [lista[i], lista[j]] = [lista[j], lista[i]];
    salvarLista(lista);
  };
  const duplicar = (i) => {
    const copia = { ...banners[i], id: `b${Date.now()}`, nomeInterno: `${banners[i].nomeInterno} (cópia)`, ativo: false };
    salvarLista([...banners.slice(0, i + 1), copia, ...banners.slice(i + 1)]);
    toast.success('Banner duplicado (desativado por padrão).');
  };
  const excluir = () => {
    salvarLista(banners.filter((b) => b.id !== excluindo.id));
    setExcluindo(null);
    if (editando?.id === excluindo.id) setEditando(null);
    toast.success('Banner removido do rascunho.');
  };

  const inputCls = 'mt-1 w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-sm outline-none focus:border-mk-blue';
  const labelCls = 'text-[11px] font-bold uppercase text-slate-400';

  return (
    <div data-testid="admin-banners" className="max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">Banners da página inicial</h1>
          <p className="mt-1 text-sm text-slate-500">Cadastre, ordene e programe os banners do carrossel (horário de Brasília).</p>
        </div>
        <button
          data-testid="banner-add-button"
          onClick={() => setEditando({ ...BANNER_VAZIO, id: `b${Date.now()}` })}
          className="inline-flex items-center gap-2 rounded-full bg-mk-red px-4 py-2.5 text-sm font-bold text-white hover:bg-mk-redbright"
        >
          <Plus size={16} /> Novo banner
        </button>
      </div>

      <div className="mt-5 space-y-3">
        {banners.length === 0 && (
          <p className="rounded-2xl border border-dashed border-slate-300 bg-white py-10 text-center text-sm text-slate-400">
            Nenhum banner no rascunho. Sem banners, a página inicial usa o banner padrão da loja.
          </p>
        )}
        {banners.map((b, i) => (
          <div key={b.id} data-testid={`banner-row-${i}`} className="rounded-2xl bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-3">
              {b.imagemDesktop ? (
                <img src={b.imagemDesktop} alt="" className="h-14 w-28 rounded-lg object-cover" />
              ) : (
                <span className="flex h-14 w-28 items-center justify-center rounded-lg bg-slate-100 text-slate-300"><ImageIcon size={20} /></span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-slate-800">{b.nomeInterno || '(sem nome)'}</p>
                <p className="text-xs text-slate-400">
                  {b.modo === 'imagem' ? 'Arte pronta' : 'Personalizável'} · {b.ativo ? 'Ativo' : 'Desativado'}
                  {b.inicio && ` · início ${new Date(b.inicio).toLocaleString('pt-BR')}`}
                  {b.fim && ` · fim ${new Date(b.fim).toLocaleString('pt-BR')}`}
                </p>
              </div>
              <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
                <input data-testid={`banner-ativo-${i}`} type="checkbox" checked={b.ativo} onChange={(e) => salvarLista(banners.map((x) => (x.id === b.id ? { ...x, ativo: e.target.checked } : x)))} className="h-4 w-4 accent-mk-blue" />
                Ativo
              </label>
              <div className="flex gap-1">
                <button data-testid={`banner-up-${i}`} onClick={() => mover(i, -1)} disabled={i === 0} aria-label="Subir" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ArrowUp size={15} /></button>
                <button data-testid={`banner-down-${i}`} onClick={() => mover(i, 1)} disabled={i === banners.length - 1} aria-label="Descer" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ArrowDown size={15} /></button>
                <button data-testid={`banner-edit-${i}`} onClick={() => setEditando({ ...b })} aria-label="Editar" className="rounded-lg p-2 text-mk-blue hover:bg-blue-50"><Pencil size={15} /></button>
                <button data-testid={`banner-dup-${i}`} onClick={() => duplicar(i)} aria-label="Duplicar" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Copy size={15} /></button>
                <button data-testid={`banner-del-${i}`} onClick={() => setExcluindo(b)} aria-label="Excluir" className="rounded-lg p-2 text-mk-red hover:bg-red-50"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editando && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-mk-bluedark/70 p-4 backdrop-blur-sm" onClick={() => setEditando(null)}>
          <div data-testid="banner-editor" className="mx-auto my-6 w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-extrabold text-slate-900">{banners.some((b) => b.id === editando.id) ? 'Editar banner' : 'Novo banner'}</h3>
              <button onClick={() => setEditando(null)} aria-label="Fechar" className="rounded-full p-2 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
            </div>

            <div className="mt-4 space-y-4">
              <div>
                <label className={labelCls}>Nome interno *</label>
                <input data-testid="banner-nome" value={editando.nomeInterno} onChange={(e) => setEditando({ ...editando, nomeInterno: e.target.value })} className={inputCls} placeholder="Ex.: Ofertas da semana" />
              </div>

              <div>
                <label className={labelCls}>Modo de criação</label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {[['imagem', 'Arte pronta'], ['personalizado', 'Personalizável']].map(([v, rot]) => (
                    <button key={v} type="button" data-testid={`banner-modo-${v}`} onClick={() => setEditando({ ...editando, modo: v })}
                      className={`rounded-xl border-2 px-3 py-2.5 text-sm font-bold transition-colors ${editando.modo === v ? 'border-mk-blue bg-blue-50 text-mk-blue' : 'border-slate-200 text-slate-500'}`}>
                      {rot}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-xs text-slate-400">
                  {editando.modo === 'imagem'
                    ? 'A arte é exibida exatamente como enviada, sem textos por cima.'
                    : 'Você envia uma foto de fundo e edita os textos e botões sobre ela (todos opcionais).'}
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <UploadBotao rotulo="Imagem para computador *" valor={editando.imagemDesktop} testid="banner-upload-desktop" onOk={(url) => setEditando({ ...editando, imagemDesktop: url })} />
                <UploadBotao rotulo="Imagem para celular (opcional)" valor={editando.imagemMobile} testid="banner-upload-mobile" onOk={(url) => setEditando({ ...editando, imagemMobile: url })} />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className={labelCls}>Enquadramento</label>
                  <select data-testid="banner-enquadramento" value={editando.enquadramento} onChange={(e) => setEditando({ ...editando, enquadramento: e.target.value })} className={inputCls}>
                    <option value="cobrir">Preencher (cobrir)</option>
                    <option value="conter">Mostrar inteira (conter)</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Posição da imagem</label>
                  <select value={editando.posicaoImagem} onChange={(e) => setEditando({ ...editando, posicaoImagem: e.target.value })} className={inputCls}>
                    <option value="centro">Centro</option>
                    <option value="topo">Topo</option>
                    <option value="base">Base</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Altura do banner</label>
                  <select data-testid="banner-altura" value={editando.altura} onChange={(e) => setEditando({ ...editando, altura: e.target.value })} className={inputCls}>
                    <option value="compacta">Compacta</option>
                    <option value="media">Média</option>
                    <option value="grande">Grande</option>
                  </select>
                </div>
              </div>

              {editando.modo === 'personalizado' && (
                <div className="space-y-4 rounded-2xl bg-slate-50 p-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div><label className={labelCls}>Título principal</label><input data-testid="banner-titulo" value={editando.titulo} onChange={(e) => setEditando({ ...editando, titulo: e.target.value })} className={inputCls} /></div>
                    <div><label className={labelCls}>Subtítulo</label><input data-testid="banner-subtitulo" value={editando.subtitulo} onChange={(e) => setEditando({ ...editando, subtitulo: e.target.value })} className={inputCls} /></div>
                  </div>
                  <div><label className={labelCls}>Descrição (opcional)</label><input value={editando.descricao} onChange={(e) => setEditando({ ...editando, descricao: e.target.value })} className={inputCls} /></div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div><label className={labelCls}>Texto do botão principal</label><input data-testid="banner-botao" value={editando.botaoPrimario} onChange={(e) => setEditando({ ...editando, botaoPrimario: e.target.value })} className={inputCls} placeholder="Ex.: Ver ofertas" /></div>
                    <div><label className={labelCls}>Botão secundário (opcional)</label><input value={editando.botaoSecundario} onChange={(e) => setEditando({ ...editando, botaoSecundario: e.target.value })} className={inputCls} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <div><label className={labelCls}>Cor do título</label><input type="color" value={editando.corTitulo} onChange={(e) => setEditando({ ...editando, corTitulo: e.target.value })} className="mt-1 h-10 w-full cursor-pointer rounded-lg border border-slate-300" /></div>
                    <div><label className={labelCls}>Cor do texto</label><input type="color" value={editando.corTexto} onChange={(e) => setEditando({ ...editando, corTexto: e.target.value })} className="mt-1 h-10 w-full cursor-pointer rounded-lg border border-slate-300" /></div>
                    <div><label className={labelCls}>Cor do botão</label><input type="color" value={editando.corBotao} onChange={(e) => setEditando({ ...editando, corBotao: e.target.value })} className="mt-1 h-10 w-full cursor-pointer rounded-lg border border-slate-300" /></div>
                    <div><label className={labelCls}>Texto do botão</label><input type="color" value={editando.corBotaoTexto} onChange={(e) => setEditando({ ...editando, corBotaoTexto: e.target.value })} className="mt-1 h-10 w-full cursor-pointer rounded-lg border border-slate-300" /></div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div>
                      <label className={labelCls}>Alinhamento</label>
                      <select value={editando.alinhamento} onChange={(e) => setEditando({ ...editando, alinhamento: e.target.value })} className={inputCls}>
                        <option value="esquerda">Esquerda</option><option value="centro">Centro</option><option value="direita">Direita</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Posição do conteúdo</label>
                      <select value={editando.posicaoVertical} onChange={(e) => setEditando({ ...editando, posicaoVertical: e.target.value })} className={inputCls}>
                        <option value="topo">Topo</option><option value="centro">Centro</option><option value="base">Base</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Sobreposição</label>
                      <select value={editando.overlay} onChange={(e) => setEditando({ ...editando, overlay: e.target.value })} className={inputCls}>
                        <option value="nenhuma">Nenhuma</option><option value="escura">Escura</option><option value="clara">Clara</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Destino do clique</label>
                  <select data-testid="banner-destino" value={editando.destinoTipo} onChange={(e) => setEditando({ ...editando, destinoTipo: e.target.value, destinoValor: '' })} className={inputCls}>
                    <option value="nenhum">Apenas imagem (sem link)</option>
                    <option value="produto">Um produto específico</option>
                    <option value="categoria">Uma categoria</option>
                    <option value="destaques">Seção de destaques</option>
                    <option value="ofertas">Seção de ofertas</option>
                    <option value="pagina">Página do catálogo (/...)</option>
                    <option value="whatsapp">Conversa no WhatsApp</option>
                    <option value="externo">Link externo</option>
                  </select>
                </div>
                <div>
                  {editando.destinoTipo === 'produto' && (
                    <>
                      <label className={labelCls}>Produto</label>
                      <select data-testid="banner-destino-produto" value={editando.destinoValor} onChange={(e) => setEditando({ ...editando, destinoValor: e.target.value })} className={inputCls}>
                        <option value="">Selecione...</option>
                        {(produtosData?.items || []).map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                      </select>
                    </>
                  )}
                  {editando.destinoTipo === 'categoria' && (
                    <>
                      <label className={labelCls}>Categoria</label>
                      <select data-testid="banner-destino-categoria" value={editando.destinoValor} onChange={(e) => setEditando({ ...editando, destinoValor: e.target.value })} className={inputCls}>
                        <option value="">Selecione...</option>
                        {(cats || []).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                      </select>
                    </>
                  )}
                  {editando.destinoTipo === 'pagina' && (
                    <>
                      <label className={labelCls}>Caminho da página</label>
                      <input value={editando.destinoValor} onChange={(e) => setEditando({ ...editando, destinoValor: e.target.value })} className={inputCls} placeholder="/" />
                    </>
                  )}
                  {editando.destinoTipo === 'externo' && (
                    <>
                      <label className={labelCls}>Link externo</label>
                      <input data-testid="banner-destino-url" value={editando.destinoValor} onChange={(e) => setEditando({ ...editando, destinoValor: e.target.value })} className={inputCls} placeholder="https://..." />
                    </>
                  )}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Início da campanha (opcional)</label>
                  <input data-testid="banner-inicio" type="datetime-local" value={paraInput(editando.inicio)} onChange={(e) => setEditando({ ...editando, inicio: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Encerramento (opcional)</label>
                  <input data-testid="banner-fim" type="datetime-local" value={paraInput(editando.fim)} onChange={(e) => setEditando({ ...editando, fim: e.target.value })} className={inputCls} />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  data-testid="banner-save"
                  onClick={() => {
                    if (!editando.nomeInterno.trim()) { toast.error('Informe o nome interno do banner.'); return; }
                    if (!editando.imagemDesktop) { toast.error('Envie a imagem do banner.'); return; }
                    const final = { ...editando, inicio: paraIso(editando.inicio), fim: paraIso(editando.fim) };
                    const existe = banners.some((b) => b.id === final.id);
                    salvarLista(existe ? banners.map((b) => (b.id === final.id ? final : b)) : [...banners, final]);
                    setEditando(null);
                    toast.success('Banner atualizado no rascunho.');
                  }}
                  className="flex-1 rounded-full bg-mk-blue py-3 text-sm font-bold text-white hover:bg-mk-bluelight"
                >
                  Aplicar ao rascunho
                </button>
                <button data-testid="banner-cancel" onClick={() => setEditando(null)} className="rounded-full border-2 border-slate-300 px-5 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {excluindo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-mk-bluedark/70 p-4 backdrop-blur-sm" onClick={() => setExcluindo(null)}>
          <div data-testid="banner-delete-modal" className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-extrabold text-slate-900">Excluir banner?</h3>
            <p className="mt-2 text-sm text-slate-500">O banner <span className="font-bold text-slate-800">"{excluindo.nomeInterno}"</span> será removido do rascunho. A exclusão só vale para o público após publicar.</p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setExcluindo(null)} className="flex-1 rounded-full border-2 border-slate-200 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button data-testid="banner-delete-confirm" onClick={excluir} className="flex-1 rounded-full bg-mk-red py-2.5 text-sm font-bold text-white hover:bg-mk-redbright">Excluir</button>
            </div>
          </div>
        </div>
      )}

      <HomeActions cfg={cfg} temAlteracoes={homeData?.temAlteracoes} />
    </div>
  );
}
